/* Hallmark · redesign: full-width theme catalogue · genre: editorial · theme: host_ui.css (designed-as-app)
 * Workbench rhythm: setup journey → search + category controls → image-led theme catalogue.
 * Theme previews stay framed but lose the card shell; each live demo floats over its cover as an explicit CTA.
 * Search is client-side and debounced so the catalogue can grow without adding a request per keystroke.
 * `?capture=theme` keeps the chrome-light preview contract and omits demo links.
 * Link step (step 2) stays canvas-style like step 1, no boxed card: Input/
 * InputGroup now default to `bg-background` against the `bg-muted` page (fixed
 * at the component level — they were `bg-transparent` and invisible on any
 * canvas without an enclosing card, e.g. this step's field and step 1's theme
 * search box), and the step narrows to `max-w-xl` so one field doesn't drown
 * in the 7xl grid width, sized up (h-14, text-lg) as the step's one primary action.
 * Hallmark · pre-emit critique: P5 H5 E5 S4 R5 V4
 */
import React from "react";
import { NavigationContext, useContent } from "@thoughtbot/superglue";
import { ExternalLinkIcon, SearchIcon } from "lucide-react";

import {
  Form,
  type FormProps,
  type RailsSubmitButtonProps,
  type RailsTextFieldProps,
} from "@javascript/components/Inputs";
import { SetupProgressRail } from "@javascript/components/SetupProgressRail";
import { Button } from "@javascript/components/ui/button";
import { Badge } from "@javascript/components/ui/badge";
import { Field, FieldDescription, FieldError, FieldLabel } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@javascript/components/ui/input-group";
import {
  TypographyH1,
  TypographyH3,
  TypographyH4,
  TypographyMuted,
} from "@javascript/components/ui/typography";
import { cn } from "@javascript/components/utils";

type WizardStep = "theme" | "slug";
type SlugAvailabilityStatus = "idle" | "available" | "taken" | "unusable";

const CATEGORY_ALL = "All";
const VISIBLE_THEME_CAP = 9;
const SEARCH_DEBOUNCE_MS = 300;

type Theme = {
  key: string;
  name: string;
  category: string | null;
  previewUrl: string;
  demoUrl: string;
  input: Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    "id" | "name" | "value" | "defaultChecked"
  >;
};
type NewInvitationContent = {
  newInvitationForm: FormProps<{
    slug: RailsTextFieldProps;
    submit: RailsSubmitButtonProps;
  }>;
  initialStep: WizardStep;
  errors: Record<string, string[]>;
  slugAvailability: {
    candidate: string;
    status: SlugAvailabilityStatus;
    message: string | null;
  };
  slugAvailabilityPath: string;
  publicUrlPrefix: string;
  themes: Theme[];
};

function themeMatchesSearch(theme: Theme, query: string): boolean {
  if (!query) return true;

  const searchableValues = [ theme.name, theme.key, theme.category ?? "" ];
  return searchableValues.some((value) => value.toLowerCase().includes(query));
}

export default function NewInvitation() {
  const {
    newInvitationForm,
    initialStep,
    errors,
    slugAvailability,
    slugAvailabilityPath,
    publicUrlPrefix,
    themes,
  } = useContent<NewInvitationContent>();
  const { remote, pageKey } = React.useContext(NavigationContext);
  const { form, extras, inputs } = newInvitationForm;
  const [step, setStep] = React.useState<WizardStep>(initialStep);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [slugCandidate, setSlugCandidate] = React.useState(
    String(inputs.slug.defaultValue ?? ""),
  );
  const [checkOnBlur, setCheckOnBlur] = React.useState(false);
  const [checkingCandidate, setCheckingCandidate] = React.useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = React.useState(CATEGORY_ALL);
  const [themeSearch, setThemeSearch] = React.useState("");
  const [debouncedThemeSearch, setDebouncedThemeSearch] = React.useState("");
  const [showAllThemes, setShowAllThemes] = React.useState(false);
  const [isCaptureMode] = React.useState(
    () => new URLSearchParams(window.location.search).get("capture") === "theme",
  );
  const slugErrors = (errors.slug ?? []).map((message) => ({ message }));
  const currentAvailability = slugAvailability.candidate === slugCandidate
    ? slugAvailability
    : null;
  const categories = React.useMemo(() => {
    const unique = Array.from(
      new Set(
        themes
          .map((theme) => theme.category)
          .filter((category): category is string => Boolean(category)),
      ),
    );
    return unique.length > 1 ? [ CATEGORY_ALL, ...unique ] : [];
  }, [ themes ]);
  const normalizedThemeSearch = themeSearch.trim().toLowerCase();
  const searchIsPending = normalizedThemeSearch !== debouncedThemeSearch;
  const categoryThemes = selectedCategory === CATEGORY_ALL
    ? themes
    : themes.filter((theme) => theme.category === selectedCategory);
  const filteredThemes = categoryThemes.filter((theme) => themeMatchesSearch(theme, debouncedThemeSearch));
  const showAllMatchingThemes = showAllThemes || Boolean(debouncedThemeSearch);
  const visibleThemes = showAllMatchingThemes ? filteredThemes : filteredThemes.slice(0, VISIBLE_THEME_CAP);
  const hiddenThemeCount = filteredThemes.length - visibleThemes.length;

  React.useEffect(() => setStep(initialStep), [initialStep]);

  React.useEffect(() => {
    const debounce = window.setTimeout(() => setDebouncedThemeSearch(normalizedThemeSearch), SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(debounce);
  }, [normalizedThemeSearch]);

  React.useEffect(() => setShowAllThemes(false), [selectedCategory, debouncedThemeSearch]);

  React.useEffect(() => {
    const resetSubmitting = () => setIsSubmitting(false);
    window.addEventListener("superglue:visit-complete", resetSubmitting);

    return () => window.removeEventListener("superglue:visit-complete", resetSubmitting);
  }, []);

  React.useEffect(() => {
    if (step !== "slug" || slugCandidate.length === 0) {
      setCheckingCandidate(null);
      return;
    }

    const candidate = slugCandidate;
    const abortController = new AbortController();
    const debounce = window.setTimeout(() => {
      const query = new URLSearchParams({
        slug_candidate: candidate,
        props_at: "data.slugAvailability",
      });

      setCheckingCandidate(candidate);
      remote(`${slugAvailabilityPath}?${query}`, {
        pageKey,
        signal: abortController.signal,
      })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            setCheckingCandidate(null);
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) setCheckingCandidate(null);
        });
    }, checkOnBlur ? 0 : 350);

    return () => {
      window.clearTimeout(debounce);
      abortController.abort();
    };
  }, [checkOnBlur, pageKey, remote, slugAvailabilityPath, slugCandidate, step]);

  const handleSubmit = () => {
    setIsSubmitting(true);
  };

  const handleContinue = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.form?.reportValidity()) return;
    setStep("slug");
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setShowAllThemes(false);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10" data-catalog-layout="full-width">
      <section
        data-capture-mode={isCaptureMode || undefined}
        className={cn("mx-auto w-full", isCaptureMode ? "max-w-5xl" : "max-w-7xl")}
      >
        <header className="border-b border-border pb-7 sm:pb-8">
          <TypographyH1 className="text-left text-4xl sm:text-5xl">New invitation</TypographyH1>
          {!isCaptureMode && (
            <TypographyMuted className="mt-3 max-w-2xl text-base leading-6">
              Choose a theme and claim your invitation link before adding your details.
            </TypographyMuted>
          )}
        </header>
        {!isCaptureMode && (
          <div className="border-b border-border py-6 sm:py-7" data-invitation-journey>
            <div className="grid gap-5 lg:grid-cols-[minmax(15rem,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:gap-10">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-0.5 font-mono text-xs font-semibold tracking-[0.2em] text-primary"
                >
                  {step === "theme" ? "01" : "02"} / 03
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                    Your setup journey
                  </p>
                  <TypographyH4 className="mt-2">
                    {step === "theme" ? "Set the mood first." : "Make it yours."}
                  </TypographyH4>
                  <p className="mt-1 max-w-sm text-sm leading-6 text-muted-foreground">
                    {step === "theme"
                      ? "Pick the visual direction, then we’ll shape the invitation around it."
                      : "Your link is the doorway guests will use to find the invitation."}
                  </p>
                </div>
              </div>
              <SetupProgressRail
                steps={[
                  { label: "Theme", state: step === "theme" ? "current" : "complete" },
                  { label: "Link", state: step === "slug" ? "current" : "upcoming" },
                  { label: "Essentials", state: "upcoming" },
                ]}
              />
            </div>
          </div>
        )}
        <Form
          {...form}
          extras={extras}
          data-sg-visit="true"
          className="mt-8 space-y-8"
          onSubmit={handleSubmit}
        >
          <fieldset hidden={step !== "theme"} className="space-y-6">
            <legend className="sr-only">Choose a theme</legend>
            <div className="grid gap-5 border-b border-border pb-6 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,24rem)] lg:items-end lg:gap-10">
              <div>
                <TypographyH3>Choose a theme</TypographyH3>
                {!isCaptureMode && (
                  <TypographyMuted className="mt-2 max-w-2xl leading-6">
                    Compare the mood, then open a live demo before you decide. You can change the theme later from your dashboard.
                  </TypographyMuted>
                )}
              </div>
              {!isCaptureMode && (
                <div className="relative">
                  <label htmlFor="theme-search" className="sr-only">Search themes</label>
                  <SearchIcon
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="theme-search"
                    type="search"
                    value={themeSearch}
                    placeholder="Search themes"
                    aria-label="Search themes"
                    aria-busy={searchIsPending}
                    className="min-h-11 pl-9"
                    onChange={(event) => setThemeSearch(event.currentTarget.value)}
                  />
                </div>
              )}
            </div>
            {!isCaptureMode && (
              <div className="flex items-center gap-3 border-l-2 border-primary bg-accent/50 px-4 py-3">
                <ExternalLinkIcon aria-hidden="true" className="size-4 shrink-0 text-primary" />
                <p className="text-sm leading-6 text-accent-foreground">
                  <span className="font-semibold">Preview before you choose.</span>{" "}
                  Every theme has a <span className="font-semibold">View live demo</span> link directly on its cover.
                </p>
              </div>
            )}
            {categories.length > 0 && (
              <div
                role="group"
                aria-label="Filter themes by category"
                className="flex flex-wrap gap-2"
              >
                {categories.map((category) => (
                  <Button
                    key={category}
                    type="button"
                    size="sm"
                    variant={selectedCategory === category ? "default" : "outline"}
                    aria-pressed={selectedCategory === category}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
              <TypographyMuted aria-live="polite">
                {searchIsPending
                  ? "Searching themes…"
                  : `${filteredThemes.length} ${filteredThemes.length === 1 ? "theme" : "themes"}`}
              </TypographyMuted>
              {hiddenThemeCount > 0 && (
                <Button type="button" variant="link" className="h-auto px-0" onClick={() => setShowAllThemes(true)}>
                  Show all {filteredThemes.length} themes
                </Button>
              )}
            </div>
            {visibleThemes.length > 0 ? (
              <div
                aria-busy={searchIsPending}
                className={cn(
                  "grid min-w-0 grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 sm:gap-x-5",
                  isCaptureMode ? "lg:grid-cols-4" : "lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6",
                )}
              >
                {visibleThemes.map((theme) => (
                  <div key={theme.key} data-theme-option className="group relative min-w-0">
                    <div
                      data-theme-cover
                      className="relative transition-[box-shadow] group-has-[:focus-visible]:ring-2 group-has-[:focus-visible]:ring-ring/50 group-has-[:checked]:ring-2 group-has-[:checked]:ring-primary/25"
                    >
                      <label
                        htmlFor={theme.input.id}
                        className="relative block cursor-pointer"
                      >
                        <img
                          src={theme.previewUrl}
                          alt={`${theme.name} preview`}
                          className="aspect-[3/4] w-full border border-border object-cover transition-transform motion-safe:group-hover:scale-[1.015] motion-reduce:transform-none motion-reduce:transition-none"
                        />
                      </label>
                      {!isCaptureMode && (
                        <a
                          href={theme.demoUrl}
                          target="_blank"
                          rel="noreferrer"
                          data-demo-action="cover-overlay"
                          className="absolute inset-x-0 top-2 z-10 flex min-h-11 items-center justify-between gap-2 border border-primary-foreground/25 bg-primary/70 px-2.5 py-2 text-xs font-semibold text-primary-foreground whitespace-nowrap shadow-lg transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:px-3 sm:text-sm"
                        >
                          <span className="flex min-w-0 items-center gap-1.5">
                            <ExternalLinkIcon aria-hidden="true" className="size-3.5 shrink-0" />
                            <span>View live demo</span>
                          </span>
                          <span aria-hidden="true" className="hidden text-base leading-none sm:inline">↗</span>
                        </a>
                      )}
                      {theme.category && (
                        <span
                          data-theme-category="price-tag"
                          className="absolute bottom-2 right-2 z-10 inline-flex min-h-7 max-w-[calc(100%-1rem)] min-w-0 items-center rounded-md bg-foreground py-1 pl-4 pr-2.5 text-[0.6875rem] font-semibold leading-none text-background shadow-md"
                        >
                          <span
                            aria-hidden="true"
                            className="absolute -left-1.5 top-1/2 size-3.5 -translate-y-1/2 rotate-45 rounded-[3px] bg-foreground"
                          />
                          <span
                            aria-hidden="true"
                            className="absolute -left-0.5 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-background"
                          />
                          <span className="relative min-w-0 truncate">{theme.category}</span>
                        </span>
                      )}
                    </div>
                    <label
                      htmlFor={theme.input.id}
                      data-theme-name
                      className="flex min-h-11 cursor-pointer items-center gap-2 px-1.5 py-2"
                    >
                      <input
                        {...theme.input}
                        type="radio"
                        required
                        className="size-4 shrink-0 cursor-pointer accent-primary"
                      />
                      <span className="truncate text-sm font-semibold text-foreground">{theme.name}</span>
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-y border-border px-4 py-10 text-center" role="status">
                <TypographyH4>No themes found</TypographyH4>
                <p className="mt-2 text-sm text-muted-foreground">Try a different name or category.</p>
                <Button type="button" variant="link" className="mt-2" onClick={() => setThemeSearch("")}>
                  Clear search
                </Button>
              </div>
            )}
          </fieldset>
          <div hidden={step !== "slug"} className="max-w-xl space-y-5">
            <div>
              <TypographyH3>Choose your invitation link</TypographyH3>
              <TypographyMuted className="mt-2 leading-6">
                Guests will visit this link to view your invitation and RSVP. Pick something memorable — you can’t change it later.
              </TypographyMuted>
            </div>
            <Field data-invalid={slugErrors.length > 0}>
              <FieldLabel htmlFor={inputs.slug.id}>Your invitation link</FieldLabel>
              <InputGroup className="h-14">
                <InputGroupAddon className="text-base md:text-base">
                  <span className="hidden sm:inline">{publicUrlPrefix}</span>
                  <span className="sm:hidden">/to/</span>
                </InputGroupAddon>
                <InputGroupInput
                  {...inputs.slug}
                  className="text-lg md:text-lg"
                  placeholder="maya-and-jules"
                  disabled={step !== "slug"}
                  aria-invalid={slugErrors.length > 0}
                  aria-describedby={`${inputs.slug.id}-description ${inputs.slug.id}-availability`}
                  onChange={(event) => {
                    setSlugCandidate(event.currentTarget.value);
                    setCheckOnBlur(false);
                  }}
                  onBlur={() => setCheckOnBlur(true)}
                />
              </InputGroup>
              <FieldDescription id={`${inputs.slug.id}-description`}>
                Use lowercase letters, numbers, and hyphens. You can’t change this later.
              </FieldDescription>
              <div id={`${inputs.slug.id}-availability`} aria-live="polite">
                {checkingCandidate === slugCandidate ? (
                  <Badge variant="outline" data-slug-availability="checking">
                    Checking availability…
                  </Badge>
                ) : currentAvailability?.message ? (
                  <Badge
                    variant={currentAvailability.status === "available" ? "success" : "destructive"}
                    data-slug-availability={currentAvailability.status}
                  >
                    {currentAvailability.message}
                  </Badge>
                ) : null}
              </div>
              <FieldError errors={slugErrors} />
            </Field>
          </div>
          {!isCaptureMode && (
            <div className={cn("flex items-center justify-end gap-3", step === "slug" && "max-w-xl")}>
              {step === "slug" && (
                <Button type="button" variant="outline" onClick={() => setStep("theme")}>
                  Back
                </Button>
              )}
              {step === "theme" ? (
                <Button type="button" size="lg" className="min-h-12 min-w-40 px-8 text-base" onClick={handleContinue}>
                  Continue
                </Button>
              ) : (
                <Button type="submit" size="lg" className="min-h-12 px-7 text-base" disabled={isSubmitting}>
                  {isSubmitting ? "Creating invitation…" : inputs.submit.text}
                </Button>
              )}
            </div>
          )}
        </Form>
      </section>
    </div>
  );
}
